import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import api from '../services/api';

const stripHtml = (html) => {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return d.textContent || '';
};

const QuestionBankPage = () => {
  const [questions, setQuestions] = useState([]);
  const [courses, setCourses]     = useState([]);
  const [topics, setTopics]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTopic, setSelectedTopic]   = useState('');

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line
  }, [selectedCourse, selectedTopic, pagination.page]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: 15 });
      if (selectedCourse) params.set('courseId', selectedCourse);
      if (selectedTopic)  params.set('topicId', selectedTopic);

      const res = await api.get(`/question-bank?${params}`);
      if (res.data.success) {
        setQuestions(res.data.data);
        setPagination(res.data.pagination);
        if (res.data.filters) {
          setCourses(res.data.filters.courses || []);
          setTopics(res.data.filters.topics || []);
        }
      }
    } catch (err) {
      console.error('Error loading questions:', err);
    }
    setLoading(false);
  };

  const filteredTopics = selectedCourse
    ? topics.filter(t => String(t.CourseId) === String(selectedCourse))
    : topics;

  const pageTitle = 'Question Bank - Free Practice Questions with Explanations | BESST';
  const pageDesc = 'Browse free practice questions with detailed explanations for CUET UG, CUET PG, NET/JRF. Each question has a detailed answer and explanation to help you prepare.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content="CUET questions, practice questions, MCQ, question bank, BESST, CUET UG, CUET PG, NET JRF, free questions, explanations" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://besst.in/question-bank" />
      </Helmet>

      <div className="min-h-screen mt-20 bg-gradient-to-br from-[#0f0526] via-[#1a0b2e] to-[#0f0526] text-white">
        {/* Hero */}
        <div className="bg-gradient-to-r from-purple-900/60 to-blue-900/40 py-12 px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">📚 Question Bank</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Free practice questions with detailed explanations. Click any question to see the full answer.
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Filters */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 mb-8 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-gray-400 text-sm mb-2">Course</label>
              <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none"
                value={selectedCourse} onChange={e => { setSelectedCourse(e.target.value); setSelectedTopic(''); setPagination(p => ({ ...p, page: 1 })); }}>
                <option value="">All Courses</option>
                {courses.map(c => <option key={c.CourseId} value={c.CourseId}>{c.CourseName}</option>)}
              </select>
            </div>
            {filteredTopics.length > 0 && (
              <div className="flex-1 min-w-[180px]">
                <label className="block text-gray-400 text-sm mb-2">Subject</label>
                <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none"
                  value={selectedTopic} onChange={e => { setSelectedTopic(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                  <option value="">All Subjects</option>
                  {filteredTopics.map(t => <option key={t.TopicId} value={t.TopicId}>{t.TopicName}</option>)}
                </select>
              </div>
            )}
            <div className="text-sm text-gray-400">{pagination.total} question{pagination.total !== 1 ? 's' : ''}</div>
          </div>

          {/* Question List */}
          {loading ? (
            <div className="text-center py-20 text-gray-400 animate-pulse">Loading questions...</div>
          ) : questions.length === 0 ? (
            <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
              <div className="text-5xl mb-4">📚</div>
              <p className="text-gray-400 text-lg">No questions available yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((q, idx) => {
                const qNum = (pagination.page - 1) * (pagination.limit || 15) + idx + 1;
                const qText = stripHtml(q.Question).substring(0, 150);
                const slug = q.slug || q.SeoSlug || `question-${q.QuesId}`;

                return (
                  <Link key={q.QuesId} to={`/questions/${slug}`}
                    className="block bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-purple-500/40 hover:bg-white/[0.07] transition-all group no-underline">
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-purple-600/30 rounded-lg flex items-center justify-center text-sm font-bold text-purple-300 shrink-0">
                        {qNum}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold text-white group-hover:text-purple-300 transition-colors leading-relaxed">
                          {qText}{qText.length >= 150 ? '...' : ''}
                        </h2>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          {q.CourseName && <span className="bg-white/10 px-2 py-1 rounded">{q.CourseName}</span>}
                          {q.TopicName && <span className="bg-white/10 px-2 py-1 rounded">{q.TopicName}</span>}
                          {q.explanation && <span className="text-green-400">💡 Explanation available</span>}
                        </div>
                      </div>
                      <span className="text-gray-500 group-hover:text-purple-300 text-lg shrink-0 mt-1">→</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button disabled={pagination.page <= 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                className="px-5 py-2.5 rounded-xl bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all">
                ← Previous
              </button>
              <span className="px-4 py-2.5 text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                className="px-5 py-2.5 rounded-xl bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all">
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuestionBankPage;